// src/pages/merchant-pages/AddShippedContainerModal.js
import { useState, useContext } from "react";
import { useTranslation } from "react-i18next";

// Assets
import saveIcon from "../../assets/save_icon.png";
import closeIcon from "../../assets/close_icon.png";

// Utils
import useLanguage from "../../utils/useLanguage";
import { DataContext } from "../../utils/DataContext";
import { onlyNums } from "../../utils/OnlyNums";
import { formatDate } from "../../utils/toDate";
import { shouldCloseModal } from "../../utils/shouldCloseModal";

// Styles
import classes from "../../components/Modal.module.css";

// Components
import ErrorBox from "../../components/ErrorBox";
import Loader from "../../components/Loader";
import CustomInput from "../../components/CustomInput";
import Dropdown from "../../components/Dropdown";
import CustomButton from "../../components/CustomButton";
import Modal from "../../components/Modal";
import HotDropdown from "../../components/HotDropdown";
import AreYouSureModal from "../../components/AreYouSureModal";

const AddShippedContainerModal = ({
  inputs,
  setInputs,
  setShowAddShippedContainerModal,
  onClose,
}) => {
  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();

  const {
    selectedSupplier,
    customers,
    getCustomers,
    error,
    setError,
    selectedCustomer,
  } = useContext(DataContext);

  const [formData, setFormData] = useState({
    customerId: selectedCustomer?.id,
    customerName: selectedCustomer?.name,
    containerNumber: "",
    expectedArrivalDate: "",
    finalDestination: "",
    shipper: "",
    material: "",
    numberOfBoxes: 0,
    measuringUnit: "",
    unitPrice: 0,
    exitDate: "",
    dateOfEntry: new Date(),
    shippingDate: "",
    permitsReceivingDate: "",
    // howWasPapersReceived: "",
    amountForSupplier: "",
    abstractAmountForCustomer: "",
    finalAmountForCustomer: "",
    notes: "",
  });

  const [delivered, setDelivered] = useState({
    title: "notDelivered",
    value: false,
  });

  const [howWasPapersReceived, setHowWasPapersReceived] = useState({
    title: "receivedByUs",
    value: "receivedByUs",
  });

  const [showAreYouSure, setShowAreYouSure] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    if (!formData.containerNumber) {
      setError("invalidContainerNumber");
    } else {
      setInputs((prev) =>
        Array.isArray(prev)
          ? [...prev, { ...formData, delivered, howWasPapersReceived }]
          : [{ ...formData, delivered, howWasPapersReceived }]
      );
      onClose();
      setError("");
    }
  };

  const [containerCustomer, setContainerCustomer] = useState(selectedCustomer);
  const [loadingModal, setLoadingModal] = useState(false);

  const handleDateChange = (e) => {
    const { name, value } = e.target;

    // Update `formData` instead of trying to modify `inputs` directly.
    setFormData((prev) => ({ ...prev, [name]: new Date(value) }));
  };

  const handleAttemptCancel = () => {
    setShowAreYouSure(true);
  };

  const handleCancel = () => {
    setShowAddShippedContainerModal(false);
    setError("");
    // setInputs([]);
  };

  const handleSelectCustomer = (customer) => {
    setContainerCustomer(customer);
    setFormData((prev) => ({
      ...prev,
      customerId: customer.id,
      customerName: customer.name,
    }));
  };

  const handleChangeOption = (option, state) => {
    if (state === "delivered") {
      setDelivered(option);
    } else if (state === "howWasPapersReceived") {
      setHowWasPapersReceived(option);
    }
  };

  return (
    <>
      <Modal
        title="addContainer"
        onClose={handleAttemptCancel}
        footer={
          <>
            {loadingModal ? <Loader /> : null}

            <section className={classes.actions}>
              <CustomButton
                onClick={handleSave}
                title={t("confirm")}
                icon={saveIcon}
                iconSize={16}
                disabled={loadingModal}
              />
              <CustomButton
                title={t("cancel")}
                onClick={handleAttemptCancel}
                variant="blank"
                icon={closeIcon}
                iconSize={14}
              />
            </section>
          </>
        }
      >
        <div
          className={`${classes.flexableForm} ${classes.flexableFormWithPadding}`}
        >
          {error === "invalidContainerNumber" && (
            <ErrorBox>{t(error)}</ErrorBox>
          )}

          <div className={classes.inputContainer}>
            <span>{t("customerName")}: </span>
            <div className={classes.invoiceNumberContainer}>
              <HotDropdown
                initOptions={customers}
                selected={
                  containerCustomer.id && containerCustomer.id !== "All"
                    ? containerCustomer
                    : { id: "", name: `--${t("chooseACustomer")}--` }
                }
                getData={getCustomers}
                setSelected={handleSelectCustomer}
                flex={true}
                errorText={error}
                optionsName={"customers"}
              />
            </div>
          </div>
          <div className={classes.inputContainer}>
            <span>{t("containerNumber")}: </span>
            <div className={classes.invoiceNumberContainer}>
              <CustomInput
                name="containerNumber"
                value={formData?.containerNumber}
                onChange={(e) => handleInputChange(e)}
                flex={true}
              />
            </div>
          </div>
          {/* <div
          className={`o-row o-row--horizontal-align_space-between o-row--gap-size_400 o-row--vertical-align_center u-width_full-width`}
        >
          <div className={classes.inputContainer}>
            <span>{t("amountForSupplier")}: </span>
            <CustomInput
              name="amountForSupplier"
              value={formData?.amountForSupplier}
              onChange={(e) => handleInputChange(e)}
              onKeyDown={(e) => onlyNums(e)}
              type="money"
              flex={true}
            />
          </div>
          <div>{selectedSupplier?.currency}</div>
        </div> */}
          <div className={classes.inputContainer}>
            <span>{t("expectedArrivalDate")}: </span>
            <CustomInput
              name="expectedArrivalDate"
              type="date"
              flex={true}
              value={formatDate(formData?.expectedArrivalDate)}
              onChange={(e) => handleDateChange(e)}
            />
          </div>
          <div className={classes.inputContainer}>
            <span>{t("finalDestination")}: </span>
            <CustomInput
              name="finalDestination"
              value={formData?.finalDestination}
              onChange={(e) => handleInputChange(e)}
              flex={true}
            />
          </div>

          <div className={classes.inputContainer}>
            <span>{t("material")}: </span>
            <CustomInput
              name="material"
              value={formData?.material}
              onChange={(e) => handleInputChange(e)}
              flex={true}
            />
          </div>
          <div className={classes.inputContainer}>
            <span>{t("numberOfBoxes")}: </span>
            <CustomInput
              name="numberOfBoxes"
              value={formData?.numberOfBoxes || ""}
              onChange={(e) => handleInputChange(e)}
              type="moneyV2"
              flex={true}
            />
          </div>
          <div className={classes.inputContainer}>
            <span>{t("measuringUnit")}: </span>
            <CustomInput
              name="measuringUnit"
              value={formData?.measuringUnit || ""}
              onChange={(e) => handleInputChange(e)}
              flex={true}
            />
          </div>
          {/* <div
          className={`o-row o-row--horizontal-align_space-between o-row--gap-size_400 o-row--vertical-align_center u-width_full-width`}
        >
          <div className={classes.inputContainer}>
            <span>{t("unitPrice")}: </span>
            <CustomInput
              name="unitPrice"
              value={formData?.unitPrice || ""}
              onChange={(e) => handleInputChange(e)}
              onKeyDown={(e) => onlyNums(e)}
              type="money"
              flex={true}
            />
          </div>
          <div>{selectedSupplier?.currency}</div>
        </div> */}
          <div className={classes.inputContainer}>
            <span>{t("shipper")}: </span>
            <CustomInput
              name="shipper"
              value={formData?.shipper}
              onChange={(e) => handleInputChange(e)}
              flex={true}
            />
          </div>
          <div className={classes.inputContainer}>
            <span>{t("dateOfEntry")}: </span>
            <CustomInput
              name="dateOfEntry"
              type="date"
              flex={true}
              value={
                formData?.dateOfEntry
                  ? formData?.dateOfEntry.toISOString().split("T")[0]
                  : ""
              }
              onChange={(e) => handleDateChange(e)}
            />
          </div>
          <div className={classes.inputContainer}>
            <span>{t("howWasPapersReceived")}: </span>
            <Dropdown
              options={[
                { title: "receivedByUs", value: "receivedByUs" },
                { title: "receivedByCustomer", value: "receivedByCustomer" },
              ]}
              selected={howWasPapersReceived}
              setSelected={(option) =>
                handleChangeOption(option, "howWasPapersReceived")
              }
              flex
            />
          </div>
          <div className={classes.inputContainer}>
            <span>{t("shippingDate")}: </span>
            <CustomInput
              name="shippingDate"
              type="date"
              flex={true}
              value={formatDate(formData?.shippingDate)}
              onChange={(e) => handleDateChange(e)}
            />
          </div>

          <div className={classes.inputContainer}>
            <span>{t("exitDate")}: </span>
            <CustomInput
              name="exitDate"
              type="date"
              flex={true}
              value={formatDate(formData?.exitDate)}
              onChange={(e) => handleDateChange(e)}
            />
          </div>
          {/* <div
          className={`o-row o-row--horizontal-align_space-between o-row--gap-size_400 o-row--vertical-align_center u-width_full-width`}
        >
          <div className={classes.inputContainer}>
            <span>{t("abstractAmountForCustomer")}: </span>
            <CustomInput
              name="abstractAmountForCustomer"
              value={formData?.abstractAmountForCustomer}
              onChange={(e) => handleInputChange(e)}
              onKeyDown={(e) => onlyNums(e)}
              type="money"
              flex={true}
            />
          </div>
          <div>{containerCustomer?.currency}</div>
        </div> */}
          <div
            className={`o-row o-row--horizontal-align_space-between o-row--gap-size_400 o-row--vertical-align_center u-width_full-width`}
          >
            <div className={classes.inputContainer}>
              <span>{t("finalAmountForCustomer")}: </span>
              <CustomInput
                name="finalAmountForCustomer"
                value={formData?.finalAmountForCustomer}
                onChange={(e) => handleInputChange(e)}
                type="moneyV2"
                flex={true}
              />
            </div>
            <div>{containerCustomer?.currency}</div>
          </div>

          <div className={classes.inputContainer}>
            <span>{t("permitsReceivingDate")}: </span>
            <CustomInput
              name="permitsReceivingDate"
              type="date"
              flex={true}
              value={formatDate(formData?.permitsReceivingDate)}
              onChange={(e) => handleDateChange(e)}
            />
          </div>

          <div className={classes.inputContainer}>
            <span>{t("deliveryStatus")}: </span>
            <Dropdown
              options={[
                { title: "delivered", value: true },
                { title: "notDelivered", value: false },
              ]}
              selected={delivered}
              setSelected={(option) => handleChangeOption(option, "delivered")}
              flex
            />
          </div>

          <div className={classes.inputContainer}>
            <span className={classes.notesClass}>{t("notes")}: </span>
            <CustomInput
              name="notes"
              type="textarea"
              value={formData?.notes || ""}
              onChange={(e) => handleInputChange(e)}
              flex={true}
            />
          </div>
          {/* <PDFUploader
            ref={pdfUploaderRef}
            invoiceNumber={data?.invoiceNumber}
            invoiceDocs={invoiceDocs}
            setInvoiceDocs={setInvoiceDocs}
            mode="upload"
            documents={data?.documents}
            tempDocs={invoiceDocs}
            setTempDocs={setInvoiceDocs}
        /> */}
        </div>
      </Modal>
      {showAreYouSure ? (
        <AreYouSureModal
          shouldCloseModal={shouldCloseModal({
            ...formData,

            // Except the below from Check
            customerId: "",
            customerName: "",
            dateOfEntry: "",
            howWasPapersReceived: "",
          })}
          onClose={() => setShowAreYouSure(false)}
          onConfirm={handleCancel}
          confirmIconType="whiteCheck"

        />
      ) : null}
    </>
  );
};

export default AddShippedContainerModal;
